import React, { useEffect } from 'react';
import { Card, Color, Dollar, Text, Flex } from 'component-library';
import { ErrorMessage } from 'components/util/ErrorMessage';
import { makeStyles } from '@material-ui/core';
import { CentsToDisplayStringNoSymbol } from 'lib/helpers';
import { CarryoverAndAdjustStatusEnum, PayrollTierEnum } from 'lib/constants';
import { observer } from 'mobx-react';
import { useTaxCreditsStores } from 'stores/useStores';
import { TaxCreditsBalanceCardContent } from 'pages/dashboard/taxCredits/TaxCreditsBalanceCardContent';
import { Form8974Tier3SideDrawer } from './Form8974Tier3SideDrawer';
import { Tier1OrTier2Form8974SideDrawer } from '../../fed-rd-program/8974/Tier1OrTier2Form8974SideDrawer';

const useStyles = makeStyles(() => ({
  dollar: {
    fontFamily: "'Alliance Platt', serif",
    fontWeight: 700,
  },
  footerContainer: {
    padding: '16px 24px 16px',
    backgroundColor: Color.neutral._light_20,
    borderTop: `1px solid ${Color.neutral._20}`,
  },
  alert: { marginTop: '24px' },
  totalCreditContainer: {
    backgroundColor: Color.blue._10,
    borderLeft: `1px solid ${Color.neutral._20}`,
  },
  tooltip: {
    verticalAlign: 'sub',
    color: '#67747D',
    display: 'inline-flex',
  },
  labelWithIcon: { display: 'flex', alignItems: 'center' },
  infoIcon: {
    fontSize: '18px',
    marginLeft: '2px',
    color: Color.neutral._60,
  },
  calculatingGraphic: {
    backgroundColor: Color.neutral._light_20,
  },
  rowBorder: {
    borderBottom: `1px solid ${Color.neutral._20}`,
    borderRight: 'none',
  },
}));

export interface CreditBalanceCardProps {
  className: string;
}

export const CreditBalanceCard = observer(
  ({ className }: CreditBalanceCardProps) => {
    const { form8974, redemption } = useTaxCreditsStores();
    const balanceCardTaxYear =
      form8974.mostRecentFinishedProgram?.taxYear || '';
    const label = `${balanceCardTaxYear} Federal R&D Credits`;
    const classes = useStyles();
    const showTier3CreditAmounts =
      form8974.payrollTier === PayrollTierEnum.TIER_3 &&
      form8974.totalRdCreditWithdrawnCents !== 0;

    useEffect(() => {
      form8974.getTotalTaxCredits();
      form8974.getAllIncomeCreditTotal();
    }, [form8974]);

    const getTierBasedFullyRedeemedMessage = (): string => {
      if (form8974.payrollTier === PayrollTierEnum.TIER_3) {
        if (!form8974.creditsFullyRedeemed) {
          return 'You have started redeeming payroll tax credits, and can redeem more next quarter.';
        }
      }
      if (
        form8974.payrollTier === PayrollTierEnum.TIER_1 ||
        form8974.payrollTier === PayrollTierEnum.TIER_2
      ) {
        return 'Your payroll provider is applying your payroll tax credits to reduce or refund your payroll taxes.';
      }

      return '';
    };

    return (
      <div data-testid={'credit-balance-card'}>
        {form8974.requestError && (
          <ErrorMessage error={form8974.requestError} />
        )}
        <Card className={className}>
          {/* {!redemption.currentFiling &&
            form8974.carryoverStatus ===
              CarryoverAndAdjustStatusEnum.PROGRAM_LEDGER_SETUP_REQUIRED && (
              <Content
                flex
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={16}
                className={classes.calculatingGraphic}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/calculating-graphic.svg`}
                  alt='Calculating graphic'
                />
                <Text size={13} variant={'medium'}>
                  We will calculate your remaining credits after you confirm
                  your tax filing date.
                </Text>
              </Content>
            )} */}

          <div>
            <Flex
              direction='column'
              padding={[16, 24]}
              className={classes.rowBorder}
            >
              <div className={classes.labelWithIcon}>
                <Text text={'Credits not yet redeemed'} size={15} />
                {/* <Tooltip
                  className={classes.tooltip}
                  text={`You have one or more actions remaining to actually use these credits`}
                  position='top'
                >
                  <Icon
                    className={classes.infoIcon}
                    name={IconEnum.info_circle}
                    color='#67747D'
                  />
                </Tooltip> */}
              </div>
              <Dollar
                value={CentsToDisplayStringNoSymbol(
                  form8974.creditBalanceCents,
                )}
                variant='medium'
                tag='h2'
              />
            </Flex>
            {form8974.totalRDCreditsRedeemedCents > 0 && (
              <Flex
                direction='column'
                padding={[16, 24]}
                className={classes.rowBorder}
              >
                <div>
                  <Text text={'Credits redeemed so far'} size={15} />
                  <Dollar
                    value={CentsToDisplayStringNoSymbol(
                      form8974.totalRDCreditsRedeemedCents,
                    )}
                    variant='medium'
                    tag='h2'
                  />
                </div>
              </Flex>
            )}
            {form8974.creditBalancePlusCreditsRedeemedCents > 0 && (
              <Flex
                direction='column'
                padding={[16, 24]}
                className={classes.totalCreditContainer}
              >
                <div>
                  <Text text='Total tax credits' size={15} />
                  <Dollar
                    value={CentsToDisplayStringNoSymbol(
                      form8974.creditBalancePlusCreditsRedeemedCents,
                    )}
                    variant='medium'
                    tag='h2'
                  />
                </div>
              </Flex>
            )}
          </div>

          {
            // All programs have been fully withdrawn
            // There are no programs that need to have their ledger set up
            // The most recent finished program has confirmed that they completed 8974 setup
            form8974.creditsFullyRedeemed && (
              <div className={classes.footerContainer}>
                <Flex padding={[0, 0, 8, 0]} direction='column' gap={8}>
                  <Text
                    text={`Your income tax credits were applied to reduce your current or future income tax liability.`}
                  />
                  <Text text={getTierBasedFullyRedeemedMessage()}></Text>
                </Flex>
              </div>
            )
          }

          {form8974.mostRecentFinishedProgram &&
            // The customer still has a redeemable balance
            // There are no programs that need to have their ledger set up
            ((form8974.creditBalanceCents > 0 &&
              form8974.carryoverStatus !==
                CarryoverAndAdjustStatusEnum.PROGRAM_LEDGER_SETUP_REQUIRED) ||
              // OR, the customer has fully withdrawn their balance
              // There are no programs that need to have their ledger set up
              // But the customer has not confirmed that they completed 8974 set up on the most recent finished program
              (form8974.creditBalanceCents <= 0 &&
                form8974.carryoverStatus ===
                  CarryoverAndAdjustStatusEnum.ZERO_BALANCE &&
                !form8974.mostRecentFinishedProgram
                  ?.payrollProviderSetupCompleted8974) ||
              redemption.currentFiling) && (
              <div className={classes.footerContainer}>
                <TaxCreditsBalanceCardContent
                  program={form8974.mostRecentFinishedProgram}
                />
              </div>
            )}
          {form8974.mostRecentFinishedProgram ? (
            form8974.payrollTier === PayrollTierEnum.TIER_3 ? (
              <Form8974Tier3SideDrawer
                program={form8974.mostRecentFinishedProgram}
              />
            ) : (
              <Tier1OrTier2Form8974SideDrawer
                program={form8974.mostRecentFinishedProgram}
                show={form8974.sideDrawerOpen}
                onCancel={() => form8974.toggleSideDrawer(false)}
                onComplete={(programId: number) =>
                  form8974.sideDrawerOnComplete(programId)
                }
              />
            )
          ) : (
            <></>
          )}
        </Card>
      </div>
    );
  },
);
